import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, LogIn, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FoxMotif from "@/components/site/FoxMotif";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin/dashboard";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter your username and password");
      return;
    }
    setBusy(true);
    try {
      await login(username, password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))] flex flex-col">
      <div className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] pf-hero-blob" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 pf-grid-bg opacity-[0.25]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 pf-noise" aria-hidden="true" />

      <header className="relative pf-container py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="admin-login-home">
          <FoxMotif size={32} testId="admin-login-fox" />
          <span className="pf-display text-[17px]">Play Fox <span className="text-[#F35A1F]">Studio</span></span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
      </header>

      <main className="relative pf-container flex-1 flex items-center justify-center pb-16">
        <div className="w-full max-w-md">
          <div
            data-testid="admin-login-card"
            className="rounded-3xl border border-[hsl(var(--border))] bg-white p-7 pf-shadow-md"
          >
            <div className="flex items-center gap-2">
              <span className="pf-mono text-[11px] uppercase tracking-wider text-[#D94A14]">Admin Access</span>
              <span className="h-px w-10 bg-[hsl(var(--border))]" />
            </div>
            <h1 className="mt-3 pf-display text-3xl tracking-tight">
              Sign in to <span className="text-[#F35A1F]">edit the site.</span>
            </h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              Hidden access for Play Fox Studio admins.
            </p>

            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div className="space-y-1.5">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  data-testid="admin-login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="admin-login-submit"
                className="h-11 w-full rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
                Sign in
              </Button>
            </form>
            <p className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">
              Tip: this page can also be reached by clicking the footer fox logo 5 times.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
